import { manifest } from "@/docs/manifest";
import { createItemizationAtom } from "@/financeModels/owasco/itemization";
import { useIsModelReadOnly } from "@/hooks/useFinancialDeckPermissions";
import {
  ActionIcon,
  Box,
  Button,
  Collapse,
  Group,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import { modals } from "@mantine/modals";
import { atom, useAtom, useAtomValue, useSetAtom } from "jotai";
import { ReactNode, useRef } from "react";
import { MdOutlinePlaylistRemove } from "react-icons/md";
import { RiFileList3Fill } from "react-icons/ri";
import { RxTriangleDown, RxTriangleRight } from "react-icons/rx";
import { AutocompleteCell, autoCompleteClassName } from "./AutocompleteCell";
import { CellRow } from "./CellRow";
import { defaultLabelColumnWidth } from "./LabelColumn";
import { LineItemLabel } from "./LineItemLabel";
import { lineSize } from "./Waterfall";
import { getFocusableElement } from "./hooks/useTabCoordinates";

const nullAtom = atom(null);

export const Itemization = ({
  itemizationAtom,
  label,
  secondaryLabel,
  tabX = 0,
  tabY,
  docs,
}: {
  itemizationAtom: ReturnType<typeof createItemizationAtom>;
  label: ReactNode;
  tabX?: number;
  tabY: number;
  docs?: {
    id: string;
    path: keyof typeof manifest;
  }[];
  secondaryLabel?: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const itemization = useAtomValue(itemizationAtom);
  const [isActive, setIsActive] = useAtom(itemization.activeAtom);
  const [isOpen, setIsOpen] = useAtom(itemization.openAtom);
  const createLineItem = useSetAtom(itemization.createLineItemAtom);
  const lineItems = itemization.lineItems;
  const [isModelReadOnly] = useIsModelReadOnly();
  const hasStaticLineItems = itemization.staticLineItems.length > 0;
  const growthRateMode = useAtomValue(
    itemization.growthRateContainer?.modeAtom || nullAtom,
  );

  if (!secondaryLabel) secondaryLabel = label;

  const openRevertLineItemsModal = async () => {
    await modals.openConfirmModal({
      title: "Are you sure you want to revert to a single entry?",

      children: (
        <Text size="md">
          Switching to a single entry will remove all itemized rows and restore
          the total value to the original single-line amount you entered.
          <br />
          <br />
          Don’t worry – we’ll save all your itemized entries. If you decide to
          go back to itemization later, everything you entered will still be
          there.
        </Text>
      ),
      labels: {
        confirm: `Revert to Single Entry`,
        cancel: "Cancel",
      },
      confirmProps: { color: "green" },
      onConfirm: async () => {
        setIsOpen(true);
        setIsActive(false);
      },
    });
  };

  const Label = (
    <Group gap={3} justify="space-between">
      <div>{label}</div>
      {isActive && (
        <Button
          size="compact-xs"
          variant="light"
          color="accent"
          rightSection={
            isOpen ? (
              <RxTriangleDown size={16} />
            ) : (
              <RxTriangleRight size={16} />
            )
          }
          onClick={() => {
            setIsOpen((o) => !o);
          }}
          maw={80}
          tt={"lowercase"}
          styles={{
            section: {
              marginInlineStart: 0,
            },
          }}
        >
          {lineItems.length} item{lineItems.length === 1 ? "" : "s"}
        </Button>
      )}
      {!isActive && !isModelReadOnly && (
        <Tooltip label="Itemize Expenses">
          <ActionIcon
            size={"sm"}
            aria-label="Itemize List"
            radius={2}
            onClick={() => {
              setIsActive(!isActive);
              setTimeout(() => {
                if (!ref || !ref.current) return;

                const ele = ref.current.querySelectorAll(
                  `.${autoCompleteClassName}`,
                )[0] as HTMLElement;

                getFocusableElement(ele)?.focus();
              }, 200);
            }}
            tabIndex={999999}
            ml="auto"
            variant="light"
            color="accent.4"
          >
            <RiFileList3Fill />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );

  return (
    <Stack gap={0} ref={ref} className="itemization">
      {!itemization.growthRateContainer && (
        <CellRow
          tabX={tabX}
          tabY={tabY + 0.0001}
          molecule={itemization.summarizedMolecule}
          label={Label}
        />
      )}

      {itemization.growthRateContainer &&
        growthRateMode === "compoundAnnual" &&
        !isActive && (
          <CellRow
            tabX={tabX}
            tabY={tabY + 0.0002}
            molecule={itemization.growthRateContainer.compoundAnnualMolecule}
            label={Label}
          />
        )}

      {itemization.growthRateContainer &&
        growthRateMode === "compoundAnnual" &&
        isActive && (
          <CellRow
            tabX={tabX}
            tabY={tabY + 0.0003}
            molecule={itemization.aggregatedColumnsMolecule}
            label={Label}
          />
        )}

      {itemization.growthRateContainer &&
        growthRateMode === "compoundAnnual" &&
        isActive && (
          <CellRow
            tabX={tabX}
            tabY={tabY + 0.0004}
            molecule={itemization.summarizedYoyMolecule}
            label={<Group gap={2}>{secondaryLabel} – Growth Rate (%)</Group>}
            variant="transparent"
          />
        )}

      {itemization.growthRateContainer &&
        growthRateMode === "perYear" &&
        !isActive && (
          <CellRow
            molecule={itemization.growthRateContainer.perYearYoYMolecule}
            label={Label}
            tabY={tabY + 0.0005}
          />
        )}

      {itemization.growthRateContainer &&
        growthRateMode === "perYear" &&
        isActive && (
          <CellRow
            tabX={tabX}
            tabY={tabY + 0.0006}
            molecule={itemization.aggregatedColumnsMolecule}
            label={Label}
          />
        )}

      {itemization.growthRateContainer && growthRateMode === "perYear" && (
        <>
          <CellRow
            tabX={tabX}
            tabY={tabY + 0.0007}
            molecule={itemization.growthRateContainer.perYearMolecule}
            label={<Group gap={2}>{secondaryLabel} – Growth Rate (%)</Group>}
            variant="transparent-label"
          />
        </>
      )}

      {isActive && (
        <Collapse in={isOpen}>
          <Box
            style={{
              boxShadow: "rgba(0, 0, 0, 0.06) 0px 2px 4px 0px inset",
              backgroundColor: "var(--mantine-color-gray-1)",
            }}
            pos="relative"
          >
            {(lineItems || []).map((lineItem, index) => (
              <CellRow
                backdropColor={"var(--mantine-color-gray-1)"}
                bg="white"
                labelWidth={defaultLabelColumnWidth - 30}
                molecule={itemization.lineItemsMolecules[index]}
                key={index}
                label={
                  <LineItemLabel
                    docPath={docs?.find((doc) => doc.id === lineItem.id)?.path}
                    lineItem={lineItem}
                    itemizationAtom={itemizationAtom}
                    editable={
                      //not read only and not a static line item
                      !isModelReadOnly &&
                      itemization.staticLineItems.findIndex(
                        (staticLineItem) => staticLineItem.id === lineItem.id,
                      ) === -1
                    }
                  />
                }
                tabY={tabY + (index + 1) * 0.001}
              />
            ))}
            {!isModelReadOnly && (
              <Group>
                <Group pos={"sticky"} left={lineSize} pl={60}>
                  {isOpen && (
                    <AutocompleteCell
                      tabY={tabY + 0.999}
                      tabX={0}
                      isFullWidth
                      onSubmit={(label) => createLineItem({ label })}
                    />
                  )}
                </Group>
                <Group pos={"sticky"} left={"calc(100% - 200px)"}>
                  {!hasStaticLineItems && (
                    <Button
                      size={"compact-xs"}
                      aria-label="Itemize List"
                      onClick={openRevertLineItemsModal}
                      tabIndex={999999}
                      leftSection={<MdOutlinePlaylistRemove size={18} />}
                      variant="light"
                      color="accent"
                      mr={6}
                    >
                      Revert to Single Line Item
                    </Button>
                  )}
                </Group>
              </Group>
            )}
          </Box>
        </Collapse>
      )}

      {itemization.growthRateContainer &&
        growthRateMode === "compoundAnnual" &&
        !isActive && (
          <>
            <CellRow
              variant="transparent"
              molecule={
                itemization.growthRateContainer.compoundAnnualYoYMolecule
              }
              label={<Group gap={2}>{secondaryLabel} – Growth Rate (%)</Group>}
              tabY={tabY + 0.001}
            />
          </>
        )}
    </Stack>
  );
};
